import React, { useState, useEffect } from 'react';
import { adminService, AuditLog, AuditLogFilters } from '../../services/adminService';
import { formatDate } from '../../utils/date';

interface AuditTabProps {
  onError: (error: string) => void;
  initialFilters?: AuditLogFilters;
}

const AuditTab: React.FC<AuditTabProps> = ({ onError, initialFilters = {} }) => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilters, setAuditFilters] = useState<AuditLogFilters>({
    page: 1,
    limit: 20,
    action: undefined,
    table_name: undefined,
    start_date: undefined,
    end_date: undefined,
    ...initialFilters,
  });
  const [auditPagination, setAuditPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });

  useEffect(() => {
    loadAuditLogs();
  }, []);

  const loadAuditLogs = async (filters: AuditLogFilters = auditFilters) => {
    try {
      setAuditLoading(true);
      onError('');
      const response = await adminService.getAuditLogs(filters);
      setAuditLogs(response.logs);
      setAuditPagination({
        currentPage: response.pagination.page,
        totalPages: response.pagination.totalPages,
        totalItems: response.pagination.total,
        itemsPerPage: response.pagination.limit,
      });
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleAuditFilterChange = (newFilters: Partial<AuditLogFilters>) => {
    const updatedFilters: AuditLogFilters = { ...auditFilters, ...newFilters, page: 1 };
    setAuditFilters(updatedFilters);
    loadAuditLogs(updatedFilters);
  };

  const handleAuditPageChange = (page: number) => {
    const updatedFilters: AuditLogFilters = { ...auditFilters, page };
    setAuditFilters(updatedFilters);
    loadAuditLogs(updatedFilters);
  };

  const getActionBadgeColor = (action: string) => {
    if (action.includes('CREATE')) return '#28a745';
    if (action.includes('UPDATE')) return '#ffc107';
    if (action.includes('DELETE')) return '#dc3545';
    if (action.includes('LOGIN')) return '#007bff';
    return '#6c757d';
  };

  return (
    <div className="card">
      <h3 style={{ margin: '0 0 1.5rem 0' }}>Audit Logs</h3>

      {/* Filters */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: 6,
        }}
      >
        <div>
          <label style={{ display: 'block', marginBottom: 8, fontSize: '0.875rem' }}>Action</label>
          <input
            type="text"
            placeholder="Filter by action..."
            value={auditFilters.action || ''}
            onChange={(e) => handleAuditFilterChange({ action: e.target.value || undefined })}
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 8, fontSize: '0.875rem' }}>Table</label>
          <select
            value={auditFilters.table_name || ''}
            onChange={(e) => handleAuditFilterChange({ table_name: e.target.value || undefined })}
            style={{ width: '100%', padding: '0.5rem' }}
          >
            <option value="">All Tables</option>
            <option value="users">Users</option>
            <option value="api_keys">API Keys</option>
            <option value="products">Products</option>
            <option value="production_orders">Production Orders</option>
            <option value="inventory">Inventory</option>
            <option value="weekly_schedules">Weekly Schedules</option>
            <option value="schedule_overrides">Schedule Overrides</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 8, fontSize: '0.875rem' }}>
            Start Date
          </label>
          <input
            type="date"
            value={auditFilters.start_date || ''}
            onChange={(e) => handleAuditFilterChange({ start_date: e.target.value || undefined })}
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 8, fontSize: '0.875rem' }}>
            End Date
          </label>
          <input
            type="date"
            value={auditFilters.end_date || ''}
            onChange={(e) => handleAuditFilterChange({ end_date: e.target.value || undefined })}
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
      </div>

      {/* Audit Logs Table */}
      {auditLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>Loading audit logs...</div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Action</th>
                <th>Table</th>
                <th>Record ID</th>
                <th>Changes</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                    No audit logs found
                  </td>
                </tr>
              ) : (
                auditLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatDate(log.created_at)}</td>
                    <td>{log.username || 'System'}</td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: getActionBadgeColor(log.action),
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: 4,
                          fontSize: '0.75rem',
                        }}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td>{log.table_name}</td>
                    <td>{log.record_id}</td>
                    <td>
                      {log.old_values || log.new_values ? (
                        <div style={{ fontSize: '0.75rem', maxWidth: '200px' }}>
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              fontSize: '0.75rem',
                              color: '#666',
                            }}
                          >
                            {JSON.stringify({ old: log.old_values, new: log.new_values }, null, 2)}
                          </pre>
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontSize: '0.75rem' }}>No changes</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {auditPagination.totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1.5rem',
          }}
        >
          <button
            className="btn btn-outline"
            onClick={() => handleAuditPageChange(auditPagination.currentPage - 1)}
            disabled={auditPagination.currentPage <= 1}
            style={{ padding: '0.5rem 1rem' }}
          >
            Previous
          </button>
          <span style={{ fontSize: '0.875rem', color: '#666' }}>
            Page {auditPagination.currentPage} of {auditPagination.totalPages} (
            {auditPagination.totalItems} total)
          </span>
          <button
            className="btn btn-outline"
            onClick={() => handleAuditPageChange(auditPagination.currentPage + 1)}
            disabled={auditPagination.currentPage >= auditPagination.totalPages}
            style={{ padding: '0.5rem 1rem' }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditTab;
